import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import './CommonStyles.css';
import { toast } from 'react-toastify';
import apiService from '../../services/api';
import dateUtils from '../../utils/dateUtils';

const emptyRow = (remainingDue) => ({
    method: 'cash',
    paidOn: dateUtils.getChicagoISOString(),
    amount: remainingDue > 0 ? remainingDue.toFixed(2) : '',
    accountId: '',
    note: '',
    cardNumber: '', cardHolderName: '', cardType: 'visa', cardMonth: '', cardYear: '', cardSecurity: '', cardTransactionNumber: '',
    chequeNumber: '', chequeExpiryDate: '', bankSubmissionDate: '',
    bankAccountNumber: '',
});

const AddPaymentModal = ({ sale, invoice, onClose, onSuccess }) => {
    const [rows, setRows] = useState([]);
    const [paymentAccounts, setPaymentAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    

    // Extract sale/invoice data
    const transactionId = sale?.id || sale?.apiData?.id;
    const customerName = sale?.customer || sale?.contact?.name || '--';
    const invoiceNo = sale?.invoice_no || sale?.ref_no || '--';
    const locationName = sale?.location?.name || '--';
    const finalTotal = parseFloat(sale?.final_total || sale?.total_payable || 0);
    const paymentDue = parseFloat(sale?.payment_due ?? finalTotal);

    // Load payment accounts + initialise first row with remaining due
    useEffect(() => {
        apiService.getPaymentAccounts()
            .then(res => setPaymentAccounts(res.data || res || []))
            .catch(() => setPaymentAccounts([]));
        setRows([emptyRow(paymentDue)]);
    }, []);

    const updateRow = (idx, field, value) => {
        setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
    };

    const addRow = () => {
        const alreadyEntered = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        const remaining = Math.max(0, paymentDue - alreadyEntered);
        setRows(prev => [...prev, emptyRow(remaining)]);
    };

    const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));

    const formatDT = (str) => str ? str.replace('T', ' ') + ':00' : null;

    const handleSave = async () => {
        setError('');
        if (!transactionId) { setError('Transaction ID is missing'); return; }
        if (rows.length === 0) { setError('Add at least one payment row'); return; }
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (!r.amount || parseFloat(r.amount) <= 0) {
                setError(`Row ${i + 1}: enter a valid amount`); return;
            }
            if (r.method === 'card' && (!r.cardNumber || !r.cardHolderName)) {
                setError(`Row ${i + 1}: card number and holder name are required`); return;
            }
            if (r.method === 'bank_transfer' && !r.bankAccountNumber) {
                setError(`Row ${i + 1}: bank account number is required`); return;
            }
        }
        try {
            setLoading(true);
            for (const r of rows) {
                const payload = {
                    transaction_id: parseInt(transactionId),
                    payment_method: r.method,
                    amount: parseFloat(r.amount),
                    paid_on: formatDT(r.paidOn),
                    payment_account_id: r.accountId || null,
                    payment_note: r.note || null,
                };
                if (r.method === 'card') {
                    payload.card_number = r.cardNumber;
                    payload.card_holder_name = r.cardHolderName;
                    payload.card_type = r.cardType;
                    if (r.cardMonth) payload.card_month = r.cardMonth;
                    if (r.cardYear) payload.card_year = r.cardYear;
                    if (r.cardSecurity) payload.card_security = r.cardSecurity;
                    if (r.cardTransactionNumber) payload.card_transaction_number = r.cardTransactionNumber;
                }
                if (r.method === 'cheque') {
                    if (r.chequeNumber) payload.cheque_number = r.chequeNumber;
                    if (r.chequeExpiryDate) payload.cheque_expiry_date = r.chequeExpiryDate;
                    if (r.bankSubmissionDate) payload.bank_submission_date = r.bankSubmissionDate;
                }
                if (r.method === 'bank_transfer') {
                    payload.bank_account_number = r.bankAccountNumber;
                }
                await apiService.addPayment(payload);
            }
            setLoading(false);
            toast.success('Payment saved successfully!');
            setTimeout(() => { if (onSuccess) onSuccess(); onClose(); }, 1200);
        } catch (err) {
            setError(err.message || 'Failed to save payments');
            setLoading(false);
        }
    };

    const inp = (style = {}) => ({ padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.82rem', color: '#334155', width: '100%', background: '#fff', ...style });
    const sel = (style = {}) => ({ ...inp(), background: '#fff', ...style });

    return (
        <>
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: '1rem' }}>
            <div style={{ background: '#fff', width: '100%', maxWidth: '960px', borderRadius: '6px', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 24px rgba(0,0,0,0.18)', maxHeight: '92vh' }}>

                {/* Header */}
                <div style={{ padding: '0.9rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ margin: 0, fontSize: '1.15rem', color: '#1e293b', fontWeight: '600' }}>Add Payment</h2>
                    <button onClick={onClose} style={{ border: 'none', background: 'transparent', color: '#9ca3af', cursor: 'pointer' }}><X size={20} /></button>
                </div>

                {/* Body */}
                <div style={{ padding: '1.25rem 1.5rem', overflowY: 'auto', flex: 1 }}>

                    {error && (
                        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: '0.65rem 1rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' }}>
                            {error}
                        </div>
                    )}

                    {/* Info boxes */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1.25rem' }}>
                        <div style={{ background: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '0.82rem', lineHeight: 1.7 }}>
                            <div><strong>Supplier:</strong> {customerName}</div>
                            <div><strong>Ref No.:</strong> {invoiceNo}</div>
                        </div>
                        <div style={{ background: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '0.82rem', lineHeight: 1.7 }}>
                            <div><strong>Location:</strong> {locationName}</div>
                        </div>
                        <div style={{ background: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '0.82rem', lineHeight: 1.7 }}>
                            <div><strong>Grand Total:</strong> $ {finalTotal.toFixed(2)}</div>
                            <div><strong>Amount Due:</strong> <span style={{ color: '#dc2626', fontWeight: 700 }}>$ {paymentDue.toFixed(2)}</span></div>
                        </div>
                    </div>

                    {/* Payment rows */}
                    {rows.map((row, idx) => (
                        <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '1rem', marginBottom: '1rem', background: '#fafafa', position: 'relative' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>Payment #{idx + 1}</span>
                                {rows.length > 1 && (
                                    <button onClick={() => removeRow(idx)} style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer', padding: '0.2rem' }}>
                                        <Trash2 size={15} />
                                    </button>
                                )}
                            </div>

                            {/* Row 1: Method | Paid On | Amount */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
                                <div>
                                    <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Payment Method:*</label>
                                    <select style={sel()} value={row.method} onChange={e => updateRow(idx, 'method', e.target.value)} disabled={loading}>
                                        <option value="cash">Cash</option>
                                        <option value="card">Card</option>
                                        <option value="cheque">Cheque</option>
                                        <option value="bank_transfer">Bank Transfer</option>
                                        <option value="credit">Credit</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Paid On:*</label>
                                    <input type="datetime-local" style={inp()} value={row.paidOn} onChange={e => updateRow(idx, 'paidOn', e.target.value)} disabled={loading} />
                                </div>
                                <div>
                                    <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Amount:*</label>
                                    <input type="number" step="0.01" min="0" style={inp()} value={row.amount} onChange={e => updateRow(idx, 'amount', e.target.value)} disabled={loading} />
                                </div>
                            </div>

                            {/* Row 2: Account | Note */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: row.method === 'card' || row.method === 'cheque' || row.method === 'bank_transfer' ? '0.75rem' : 0 }}>
                                <div>
                                    <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Payment Account:</label>
                                    <select style={sel()} value={row.accountId} onChange={e => updateRow(idx, 'accountId', e.target.value)} disabled={loading}>
                                        <option value="">None</option>
                                        {paymentAccounts.map(acc => (
                                            <option key={acc.id} value={acc.id}>
                                                {acc.name || acc.account_name || acc.display_name || `Account #${acc.id}`}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Payment Note:</label>
                                    <input type="text" style={inp()} value={row.note} onChange={e => updateRow(idx, 'note', e.target.value)} placeholder="Optional note" disabled={loading} />
                                </div>
                            </div>

                            {/* Card extra fields */}
                            {row.method === 'card' && (
                                <div style={{ background: '#f1f5f9', borderRadius: '4px', padding: '0.75rem', marginTop: '0.75rem' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                        <div>
                                            <label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Card Number:*</label>
                                            <input type="text" style={inp()} value={row.cardNumber} onChange={e => updateRow(idx, 'cardNumber', e.target.value)} placeholder="1234 5678 9012 3456" />
                                        </div>
                                        <div>
                                            <label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Card Holder:*</label>
                                            <input type="text" style={inp()} value={row.cardHolderName} onChange={e => updateRow(idx, 'cardHolderName', e.target.value)} placeholder="John Doe" />
                                        </div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '0.5rem' }}>
                                        <div>
                                            <label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Type</label>
                                            <select style={sel()} value={row.cardType} onChange={e => updateRow(idx, 'cardType', e.target.value)}>
                                                <option value="visa">Visa</option>
                                                <option value="mastercard">Mastercard</option>
                                                <option value="amex">Amex</option>
                                                <option value="discover">Discover</option>
                                            </select>
                                        </div>
                                        <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Month</label><input type="text" maxLength="2" style={inp()} value={row.cardMonth} onChange={e => updateRow(idx, 'cardMonth', e.target.value)} placeholder="MM" /></div>
                                        <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Year</label><input type="text" maxLength="4" style={inp()} value={row.cardYear} onChange={e => updateRow(idx, 'cardYear', e.target.value)} placeholder="YYYY" /></div>
                                        <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>CVV</label><input type="text" maxLength="4" style={inp()} value={row.cardSecurity} onChange={e => updateRow(idx, 'cardSecurity', e.target.value)} placeholder="123" /></div>
                                        <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Txn #</label><input type="text" style={inp()} value={row.cardTransactionNumber} onChange={e => updateRow(idx, 'cardTransactionNumber', e.target.value)} /></div>
                                    </div>
                                </div>
                            )}

                            {/* Cheque extra fields */}
                            {row.method === 'cheque' && (
                                <div style={{ background: '#f1f5f9', borderRadius: '4px', padding: '0.75rem', marginTop: '0.75rem' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                                        <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Cheque Number</label><input type="text" style={inp()} value={row.chequeNumber} onChange={e => updateRow(idx, 'chequeNumber', e.target.value)} /></div>
                                        <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Cheque Expiry</label><input type="date" style={inp()} value={row.chequeExpiryDate} onChange={e => updateRow(idx, 'chequeExpiryDate', e.target.value)} /></div>
                                        <div><label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Bank Submission Date</label><input type="date" style={inp()} value={row.bankSubmissionDate} onChange={e => updateRow(idx, 'bankSubmissionDate', e.target.value)} /></div>
                                    </div>
                                </div>
                            )}

                            {/* Bank Transfer extra fields */}
                            {row.method === 'bank_transfer' && (
                                <div style={{ background: '#f1f5f9', borderRadius: '4px', padding: '0.75rem', marginTop: '0.75rem' }}>
                                    <label style={{ fontWeight: 600, fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>Bank Account Number:*</label>
                                    <input type="text" style={inp({ maxWidth: '320px' })} value={row.bankAccountNumber} onChange={e => updateRow(idx, 'bankAccountNumber', e.target.value)} placeholder="Enter bank account number" />
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Add Payment Row button */}
                    <button
                        onClick={addRow}
                        disabled={loading}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#10b981', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.45rem 1rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginBottom: '0.5rem' }}
                    >
                        <Plus size={15} /> Add Payment
                    </button>

                </div>

                {/* Footer */}
                <div style={{ padding: '0.9rem 1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'center', gap: '0.75rem', background: '#fff', borderRadius: '0 0 6px 6px' }}>
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        style={{ background: loading ? '#9ca3af' : '#581c87', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.5rem 1.5rem', fontSize: '0.875rem', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', minWidth: '90px' }}
                    >
                        {loading ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={onClose} disabled={loading} style={{ background: '#374151', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.5rem 1.5rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer', minWidth: '90px' }}>
                        Close
                    </button>
                </div>

            </div>
        </div>
        </>
    );
};

export default AddPaymentModal;
